import React, {useEffect, useState} from 'react';
import {Chip, Collapse, Paper, Stack, Typography} from '@mui/material';
import CoverageHoverBar from './CoverageHoverBar';
import ConflictBadge from './ConflictBadge';
import ExcerptList from './ExcerptList';

export function questionAnchorId(questionId) {
    return questionId ? `coverage-question-${questionId}` : '';
}

function SourceChip({question}) {
    const label = question.source === 'EEI'
        ? (question.eeiNumber || 'EEI')
        : (question.icrNumber || 'ICR');
    return (
        <Chip
            size="small"
            label={label}
            variant="outlined"
            sx={{height: 20, fontSize: '0.65rem', fontFamily: 'Roboto Mono, monospace'}}
        />
    );
}

function ArRow({ar, onNavigateToConflict, onOpenReport}) {
    return (
        <Paper
            variant="outlined"
            data-testid={`ar-row-${ar.id}`}
            onClick={(e) => e.stopPropagation()}
            sx={{p: 1.25}}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap>
                    <Typography variant="body2" sx={{flex: 1, minWidth: 0, lineHeight: 1.45}}>
                        {ar.text}
                    </Typography>
                    {ar.conflictIds.length > 0 && (
                        <ConflictBadge
                            clickable
                            tooltip="View this conflict"
                            onClick={() => onNavigateToConflict?.(ar.conflictIds[0])}
                        />
                    )}
                </Stack>
                <CoverageHoverBar
                    value={ar.coveragePct}
                    explanation={ar.explanation}
                    excerpt={ar.excerpts[0] || null}
                />
                {ar.excerpts.length > 0 && (
                    <ExcerptList dense excerpts={ar.excerpts} onOpenReport={onOpenReport}/>
                )}
            </Stack>
        </Paper>
    );
}

function QuestionCard({
    question,
    active,
    onNavigateToConflict,
    onOpenReport,
}) {
    const [expanded, setExpanded] = useState(false);
    const hasArs = question.ars.length > 0;
    const open = hasArs && (expanded || active);
    const arLabel = question.ars.length === 1
        ? '1 analytic requirement'
        : `${question.ars.length} analytic requirements`;

    useEffect(() => {
        if (active && hasArs) {
            setExpanded(true);
        }
    }, [active, hasArs]);

    return (
        <Paper
            id={questionAnchorId(question.id)}
            variant="outlined"
            onClick={hasArs ? () => setExpanded((prev) => !prev) : undefined}
            sx={{
                p: 1.5,
                cursor: hasArs ? 'pointer' : 'default',
                borderColor: active ? 'primary.main' : undefined,
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="flex-start" useFlexGap>
                    <SourceChip question={question}/>
                    <Typography variant="body2" fontWeight={600} sx={{flex: 1, minWidth: 0, lineHeight: 1.45}}>
                        {question.text}
                    </Typography>
                    {question.hasConflict && (
                        <ConflictBadge
                            clickable={!hasArs && question.conflictIds.length > 0}
                            tooltip={hasArs
                                ? 'Conflicting FINTEL statements — expand the question to open the conflict from its analytic requirement'
                                : 'View this conflict'}
                            onClick={!hasArs && question.conflictIds.length > 0
                                ? () => onNavigateToConflict?.(question.conflictIds[0])
                                : undefined}
                        />
                    )}
                </Stack>

                <CoverageHoverBar
                    value={question.coveragePct}
                    explanation={question.explanation}
                    excerpt={hasArs ? null : (question.excerpts[0] || null)}
                />

                {!hasArs && question.excerpts.length > 0 && (
                    <ExcerptList dense excerpts={question.excerpts} onOpenReport={onOpenReport}/>
                )}

                {hasArs && (
                    <>
                        <Typography variant="caption" color="primary.main">
                            {open ? 'Hide' : 'Show'} {arLabel}
                        </Typography>
                        <Collapse in={open}>
                            <Stack spacing={1} sx={{mt: 0.5}}>
                                {question.ars.map((ar) => (
                                    <ArRow
                                        key={ar.id}
                                        ar={ar}
                                        onNavigateToConflict={onNavigateToConflict}
                                        onOpenReport={onOpenReport}
                                    />
                                ))}
                            </Stack>
                        </Collapse>
                    </>
                )}
            </Stack>
        </Paper>
    );
}

/**
 * Questions tab: the ICR question + EEI questions with per-question coverage,
 * expandable per-AR breakdown, rich hover explanations, CONFLICT badges, and
 * short FINTEL excerpts.
 */
export default function AnalyticQuestionsTab({
    view,
    activeQuestionId = null,
    onNavigateToConflict,
    onOpenReport,
}) {
    useEffect(() => {
        if (!activeQuestionId) {
            return undefined;
        }
        const timer = setTimeout(() => {
            document.getElementById(questionAnchorId(activeQuestionId))
                ?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }, 100);
        return () => clearTimeout(timer);
    }, [activeQuestionId]);

    if (!view?.questions?.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No questions available. Analytic coverage has not produced a question list yet.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            {view.questions.map((question) => (
                <QuestionCard
                    key={question.id}
                    question={question}
                    active={activeQuestionId != null && question.id === activeQuestionId}
                    onNavigateToConflict={onNavigateToConflict}
                    onOpenReport={onOpenReport}
                />
            ))}
        </Stack>
    );
}
